import usePromptModal, {
  PromptModalType,
} from '@op/components/GPT/hooks/usePromptModal';
import {
  useKeyOpinionPromptList,
  getVideoPromptList,
} from '@op/components/GPT';
import { localeType } from '@home/components/RegionSelect/type';
import useAdvancedSummary from '@op/components/GPT/hooks/useAdvancedSummary';
import { useConfirm } from '@op/components/GPT/hooks/useConfirm';
import {
  GPTType,
  PromptConfirmItemType,
  PromptListType,
} from '@op/components/GPT/types';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useIntl, getLocale, useParams } from 'umi';
import {
  GPTContentType,
  KeywordUrlPromptType,
  KeywordUrlTabType,
  PromptKeyType,
  URLListType,
} from '../types';
import _, { has } from 'lodash-es';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { checkUrlPattern, getSummaryUpdateLog } from '@op/services';
import { getDefaultPromptMap } from '@op/components/GPT/utils';
import { useStateRef } from '@op/hooks/common';
import useLanguage from '@op/hooks/useLanguage';
import { regularMaximumDuration } from '../constants';
import { DEFAULT_MODAL } from '@op/components/GPT/constants';

export function useFilterUrlList() {
  const { checkUrlList, loading } = useCheckUrlPattern();

  const [urlList, setUrlList] = useState<URLListType[]>([
    { text: '', value: 0, status: 'success' },
  ]);

  const { urlDisabled, hasUrlList } = useMemo(() => {
    let hasUrlList = false;
    const errorList = urlList.map(item => {
      const url = item?.text?.trim();
      if (!hasUrlList) {
        hasUrlList = !!url;
      }
      return item?.status !== 'success';
    });

    return {
      urlDisabled: errorList.includes(true),
      hasUrlList,
    };
  }, [urlList]);

  const getUrlFilter = () => {
    const list = urlList.map(item => item?.text).filter(Boolean);
    return list;
  };

  async function checkUrlListFunc() {
    const list = getUrlFilter();
    const data = await checkUrlList(list);

    if (data.length > 0) {
      const list = urlList.map(item => {
        if (data.includes(item.text)) {
          item.status = 'error';
        }
        return item;
      });
      setUrlList(list);
      return false;
    }

    return true;
  }

  const resetUrlList = () => {
    setUrlList([{ text: '', value: 0, status: 'success' }]);
  };

  function initUrlList(data: any) {
    const list = data?.content_urls;
    if (!list && !Array.isArray(list)) return;
    let result = list.slice(0, 10).map((item: string, index: number) => ({
      text: item,
      value: index,
      status: 'success',
    }));
    if (result.length === 0) {
      result = [{ text: '', value: 0, status: 'success' }];
    }
    setUrlList(result);
  }

  return {
    urlList,
    setUrlList,
    getUrlFilter,
    urlDisabled,
    resetUrlList,
    hasUrlList,
    checkUrlListFunc,
    loading,
    initUrlList,
  };
}

export function useFilterGptInfo({
  wholeGameInfo,
  gameInfo,
  updateSummary,
  setSaveParams,
  isPreviews = false,
  defaultTab,
}: {
  wholeGameInfo: any;
  gameInfo: any;
  updateSummary: (value: any) => boolean;
  setSaveParams: React.Dispatch<any>;
  isPreviews?: boolean;
  defaultTab?: KeywordUrlTabType;
}) {
  const [tab, setTab] = useState<KeywordUrlTabType>(defaultTab || 'keyword');
  const [filterValue, setFilterValue] = useState<any>(null);
  const [oriFilterValue, setOriFilterValue] = useState<any>(null);
  const [gptListRef, gptList, setGptList] = useStateRef<GPTContentType[]>([]);

  const [promptInfoMap, setPromptInfoMap] = useState<KeywordUrlPromptType>({
    keyword: {
      gptOptions: 'basic',
      resultType: 'page',
      prompt: '',
      modelType: DEFAULT_MODAL,
      businessType: 'keyword_analysis',
    },
    comments: {
      gptOptions: 'basic',
      resultType: 'page',
      prompt: '',
      modelType: DEFAULT_MODAL,
    },
    video: {
      gptOptions: 'basic',
      resultType: 'page',
      prompt: '',
      modelType: DEFAULT_MODAL,
    },
  });

  function changeFilterValue(
    filterValue: any,
    oriFilterValue: any,
    tab: KeywordUrlTabType,
  ) {
    setFilterValue(filterValue);
    setOriFilterValue(oriFilterValue);
    setTab(tab);
  }
  const promptList = useKeyOpinionPromptList({ gameInfo: wholeGameInfo });

  const videoPromptList = useMemo(() => {
    const videoPromptList = getVideoPromptList();

    return videoPromptList;
  }, []);

  async function changePromptInfo(
    promptInfo: KeywordUrlPromptType,
    params?: { [key: string]: string },
  ) {
    const promptMap = { ...promptInfoMap, ...promptInfo };
    setPromptInfoMap(promptMap);
    let flag = false;
    if (params) {
      const shortId = await asyncCreateShortUrl({
        filter: { ...oriFilterValue, promptInfo: promptMap },
        gameInfo,
        updateUrl: !isPreviews,
      });

      if (!shortId) return false;

      if (defaultTab === 'keyword') {
        setSaveParams((prev: any) => {
          return { ...prev, ...params, short_url: shortId };
        });
      }

      flag = await updateSummary({ ...params, short_url: shortId });
    }
    return flag;
  }

  return {
    promptInfoMap,
    changePromptInfo,
    promptList,
    videoPromptList,
    oriFilterValue,
    filterValue,
    tab,
    changeFilterValue,
    gptListRef,
    gptList,
    setGptList,
  };
}

export function useFilterPromptInfo({
  tab,
  wholeGameInfo,
  gameInfo,
  promptList,
  videoPromptList,
  needRequest,
}: {
  tab: KeywordUrlTabType;
  wholeGameInfo: any;
  gameInfo: any;
  promptList: PromptListType;
  videoPromptList: PromptListType;
  needRequest?: boolean;
}) {
  const { formatMessage } = useIntl();
  const [gptType, setGptType] = useState<GPTType>(DEFAULT_MODAL);
  const [currentTab, setCurrentTab] = useState<GPTContentType>('comments');
  const [isAdvanced, setIsAdvanced] = useState<boolean>(false);

  const {
    promptInfo: commentsPromptInfo,
    setPromptInfo: setCommentsPromptInfo,
    visible,
    toggleVisible,
  } = usePromptModal({
    defaultPromptInfo: {
      gptOptions: 'basic',
      resultType: 'page',
      prompt: '',
      advancedPrompt: '',
    },
  });

  const { promptInfo: videoPromptInfo, setPromptInfo: setVideoPromptInfo } =
    usePromptModal({
      defaultPromptInfo: {
        gptOptions: 'basic',
        resultType: 'page',
        prompt: '',
        advancedPrompt: '',
      },
    });

  const { promptInfo: keywordPromptInfo, setPromptInfo: setKeywordPromptInfo } =
    usePromptModal({
      defaultPromptInfo: {
        gptOptions: 'basic',
        resultType: 'page',
        prompt: '',
        advancedPrompt: '',
      },
    });

  // 初始化promptInfo
  useEffect(() => {
    const defaultValueInfo = getDefaultPromptMap(promptList, 1, gptType);
    const videoValueInfo = getDefaultPromptMap(videoPromptList, 1, gptType);
    [
      {
        data: commentsPromptInfo,
        setValue: setCommentsPromptInfo,
        promptInfo: defaultValueInfo,
      },
      {
        data: videoPromptInfo,
        setValue: setVideoPromptInfo,
        promptInfo: videoValueInfo,
      },
      {
        data: keywordPromptInfo,
        setValue: setKeywordPromptInfo,
        promptInfo: defaultValueInfo,
      },
    ].map(({ data, setValue, promptInfo }) => {
      if (
        !promptInfo &&
        data?.prompt !== '' &&
        promptInfo?.['en-US'] !== data?.prompt &&
        promptInfo?.['zh-CN'] !== data?.prompt
      ) {
        // eslint-disable-next-line array-callback-return
        return;
      }
      const local: localeType = getLocale();
      setValue({ ...data, prompt: promptInfo?.[local] });
    });
  }, [formatMessage, gptType, promptList, videoPromptList]);

  const {
    remainder,
    total,
    setRemainder,
    loading: advancedLimitLoading,
  } = useAdvancedSummary({ needRequest });

  const { advancedConfirm } = useConfirm({
    setRemainder,
    gameInfo,
    filter: {},
  });

  function toggleUpdateVisible(
    tab: GPTContentType = 'comments',
    isAdvanced = false,
  ) {
    if (!visible) {
      setIsAdvanced(isAdvanced);
      setCurrentTab(tab);
    }
    toggleVisible();
  }

  const promptInfo = useMemo(() => {
    if (tab === 'keyword') return keywordPromptInfo;

    if (currentTab === 'comments') return commentsPromptInfo;

    return videoPromptInfo;
  }, [tab, currentTab, videoPromptInfo, commentsPromptInfo, keywordPromptInfo]);

  function setPromptInfo(
    data: PromptConfirmItemType,
    type: Array<PromptKeyType> | PromptKeyType = currentTab,
  ) {
    const keys = Array.isArray(type) ? type : [type];
    keys.forEach(item => {
      if (item === 'keyword') {
        setKeywordPromptInfo({ ...keywordPromptInfo, ...data });
      } else if (item === 'comments') {
        setCommentsPromptInfo({ ...commentsPromptInfo, ...data });
      } else if (item === 'video') {
        setVideoPromptInfo({ ...videoPromptInfo, ...data });
      }
    });
  }

  function initPromptInfo(data: any) {
    const promptInfo = data?.promptInfo;
    if (!promptInfo) return;
    // 新版本的promptInfo
    if (
      has(promptInfo, 'keyword') ||
      has(promptInfo, 'video') ||
      has(promptInfo, 'comments')
    ) {
      // eslint-disable-next-line no-restricted-syntax
      for (const key in promptInfo) {
        const element = promptInfo[key];
        setPromptInfo(element, key as PromptKeyType);
      }
      return;
    }
    setPromptInfo(promptInfo, 'keyword');
  }

  const [gptList, setGptList] = useState<Array<GPTContentType>>(['comments']);

  function resetPromptInfo() {
    setPromptInfo(
      {
        gptOptions: 'basic',
        resultType: 'page',
      },
      ['comments', 'keyword', 'video'],
    );
    setGptType(DEFAULT_MODAL);
    setGptList(['comments'] as any);
  }

  const modalTitle = useMemo(() => {
    if (tab === 'keyword') return undefined;

    return currentTab === 'comments'
      ? 'Comments Prompt Modification'
      : 'Video Prompt Modification';
  }, [currentTab, tab]);

  function filterPromptInfo(
    promptInfo?: PromptModalType,
    filterPrompt = false,
  ) {
    const { prompt, advancedPrompt, ...info } = promptInfo || {};

    if (filterPrompt) return info;

    return promptInfo;
  }

  function getPromptInfo(filterPrompt = false) {
    if (tab === 'keyword') {
      const item = filterPromptInfo(keywordPromptInfo, filterPrompt);

      return { keyword: { ...item, modelType: gptType } };
    }

    const commonItem = filterPromptInfo(commentsPromptInfo, filterPrompt);
    const videoItem = filterPromptInfo(videoPromptInfo, filterPrompt);
    return {
      comments: { ...commonItem, modelType: gptType },
      video: { ...videoItem, modelType: gptType },
    };
  }

  const realPromptList = useMemo(() => {
    if (currentTab === 'video') {
      return videoPromptList;
    }

    return promptList;
  }, [currentTab, promptList, videoPromptList]);

  function getPromptRealDetail(item?: PromptModalType) {
    const { advancedPrompt, prompt, gptOptions, ...data } = item || {};
    return {
      ...data,
      gptOptions,
      prompt: gptOptions === 'basic' ? prompt : advancedPrompt,
    };
  }

  return {
    videoPromptInfo,
    commentsPromptInfo,
    visible,
    toggleVisible: toggleUpdateVisible,
    promptInfo,
    setPromptInfo,
    keywordPromptInfo,
    setKeywordPromptInfo,
    initPromptInfo,
    modalTitle,
    gptType,
    setGptType,
    remainder,
    total,
    advancedLimitLoading,
    advancedConfirm,
    getPromptInfo,
    resetPromptInfo,
    gptList,
    setGptList,
    realPromptList,
    isAdvanced,
    getPromptRealDetail,
  };
}

export function useBtnFilter({
  tab,
  grammarTrue,
  urlDisabled,
  hasUrlList,
  hasPermission,
}: {
  tab: KeywordUrlTabType;
  grammarTrue: boolean;
  urlDisabled: boolean;
  hasUrlList: boolean;
  hasPermission?: boolean;
}) {
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const isGenerated = useRef<boolean>(false);
  const { formatMessage } = useLanguage();
  const [disabled, setDisabled] = useState<boolean>(false);
  const maximumDuration: any = useMemo(() => {
    if (tab === 'keyword') return undefined;

    if (tab === 'topic') {
      if (hasPermission) return undefined;
      return regularMaximumDuration(formatMessage);
    }

    return {
      defaultNum: 90,
      defaultUnit: 'days',
      tips: formatMessage(
        'Only supports URLs within the last 90 days. Please reselect the date',
      ),
    };
  }, [tab, formatMessage, hasPermission]);

  const confirmDisabled = useMemo(() => {
    if (!grammarTrue) return true;
    if (tab === 'url') {
      return urlDisabled || !hasUrlList;
    }
    return disabled;
  }, [hasUrlList, disabled, urlDisabled, tab, grammarTrue]);

  return {
    confirmDisabled,
    saveDisabled,
    setSaveDisabled,
    isGenerated,
    setDisabled,
    maximumDuration,
  };
}

export function useCheckUrlPattern() {
  const { wrapperRequestTile, loading } = useCancelRequestHook<Array<string>>();
  function checkUrlList(url: string | Array<string>) {
    const list = Array.isArray(url) ? url : [url];

    return wrapperRequestTile(checkUrlPattern, { content_urls: list }).then(
      data => data || [],
    );
  }

  return {
    loading,
    checkUrlList,
  };
}

export function useSummaryUpdateLog({
  gameInfo,
  tab,
}: {
  gameInfo: any;
  tab: any;
}) {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [resetDisabled, setResetDisabled] = useState(true);

  function getSummaryLog() {
    const realId = Number(id);
    if (!id || isNaN(realId)) return;
    setLoading(true);
    getSummaryUpdateLog({ ...gameInfo, id: realId, business_type: tab })
      .then((data: { count: number }) => {
        if (data?.count >= 1) {
          setResetDisabled(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return {
    loading,
    resetDisabled,
    getSummaryLog,
    setLoading,
  };
}
